import React from "react";
import { Heart, MessageCircle, Check, Sparkles, Users } from "lucide-react";

/**
 * Lightweight, brand-styled representations of in-app screens.
 * These stand in until real product screenshots are supplied.
 */

const ScreenShell = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="flex h-full flex-col bg-background">
    <div className="border-b border-border px-4 pb-3 pt-6">
      <p className="font-display uppercase tracking-[0.18em] text-[11px] text-slate">Vent</p>
      <p className="font-display text-lg uppercase tracking-tight text-foreground">{title}</p>
    </div>
    <div className="flex-1 space-y-3 overflow-hidden p-3">{children}</div>
  </div>
);

const realRooms = [
  {
    name: "Accomplishments",
    tags: ["Positivity"],
    members: 102,
    replies: 17,
    blurb: "Share your wins, big or small! A space to celebrate and inspire others.",
  },
  {
    name: "Addiction",
    tags: ["Support", "Recovery"],
    members: 70,
    replies: 15,
    blurb: "A safe space to share your journey with recovery and the challenges along the way.",
  },
  {
    name: "Anxiety & Stressors",
    tags: ["Mental Health", "Anxiety"],
    members: 325,
    replies: 68,
    blurb: "Connect with others experiencing anxiety and depression.",
  },
];

export const RoomsScreen = () => (
  <ScreenShell title="Rooms">
    {realRooms.map((room) => (
      <div key={room.name} className="rounded-lg border border-border p-3">
        <div className="flex items-start justify-between gap-2">
          <p className="text-[11px] font-semibold text-foreground">{room.name}</p>
          <div className="flex flex-col items-end gap-0.5 text-[9px] text-muted-foreground">
            <span className="flex items-center gap-1">
              <Users className="h-2.5 w-2.5" /> {room.members}
            </span>
            <span className="flex items-center gap-1">
              <MessageCircle className="h-2.5 w-2.5" /> {room.replies}
            </span>
          </div>
        </div>
        <div className="mt-1.5 flex flex-wrap gap-1">
          {room.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-mist px-2 py-0.5 text-[8px] font-medium text-ink"
            >
              {tag}
            </span>
          ))}
        </div>
        <p className="mt-1.5 line-clamp-2 text-[9px] leading-relaxed text-muted-foreground">
          {room.blurb}
        </p>
        <div className="mt-2 flex justify-end">
          <span className="rounded-md bg-mist px-2 py-1 text-[9px] font-medium text-ink">
            Joined
          </span>
        </div>
      </div>
    ))}
  </ScreenShell>
);


export const VentScreen = () => (
  <ScreenShell title="Home">
    <div className="rounded-lg border border-border p-3">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="h-4 w-4 rounded-full bg-mist" />
        <p className="text-[10px] font-medium text-brand">happy-seal-5976</p>
        <span className="text-[9px] text-muted-foreground">in</span>
        <span className="rounded-full bg-mist px-2 py-0.5 text-[8px] font-medium text-ink">
          Childhood/Adolescent Upbringing
        </span>
        <span className="text-[9px] text-muted-foreground">6 months ago</span>
      </div>
      <div className="mt-1.5">
        <span className="rounded-full bg-mist px-2 py-0.5 text-[8px] font-medium text-ink">
          Just Venting
        </span>
      </div>
      <p className="mt-2 text-[10px] leading-relaxed text-foreground">
        Life has been so happy lately. I find myself smiling for no reason, and I love that
        feeling. We don't talk enough about the happy seasons in life, and I think we should — it
        attracts more of it. I used to be afraid to speak on my happiness, thinking that if I said
        it out loud, it might go away. But I decided to take the risk... and it paid off. Life is a
        happy place. Not perfect, but happy — if you choose to look for it. ✨ What's your happy
        place or your happy season right now? 😊
      </p>
      <div className="mt-3 flex items-center gap-4 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1">
          <Heart className="h-3 w-3 fill-red-500 text-red-500" /> 44
        </span>
        <span className="flex items-center gap-1">
          <MessageCircle className="h-3 w-3" /> 3 Replies
        </span>
      </div>
    </div>
    <div className="ml-4 rounded-lg border-l-2 border-border bg-paper p-3">
      <div className="flex items-center gap-1.5">
        <span className="h-4 w-4 rounded-full bg-mist" />
        <p className="text-[10px] font-medium text-brand">bright-penguin-7418</p>
        <span className="text-[9px] text-muted-foreground">6 months ago</span>
      </div>
      <p className="mt-1 text-[10px] leading-relaxed text-foreground">
        This is literally awesome and I am happy just reading this and you are so correct. We do
        need to talk about the happy times because sometimes it may not last forever. I say my happy
        time is when I am with my wife and family I feel like I am in a whole different world wi...
      </p>

      <div className="mt-2 flex items-center gap-4 text-[9px] text-muted-foreground">
        <Heart className="h-3 w-3" />
        <span className="flex items-center gap-1">
          <MessageCircle className="h-3 w-3" /> Reply
        </span>
      </div>
    </div>
    <div className="rounded-lg border border-border px-3 py-2 text-center text-[10px] text-muted-foreground">
      Write a reply...
    </div>
  </ScreenShell>
);

export const ReplyScreen = () => (
  <ScreenShell title="Replies">
    <div className="rounded-lg border border-border p-3">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="h-4 w-4 rounded-full bg-mist" />
        <p className="text-[10px] font-medium text-brand">happy-seal-5976</p>
        <span className="text-[9px] text-muted-foreground">in</span>
        <span className="rounded-full bg-mist px-2 py-0.5 text-[8px] font-medium text-ink">
          Just Venting
        </span>
        <span className="text-[9px] text-muted-foreground">6 months ago</span>
      </div>
      <p className="mt-2 text-[10px] leading-relaxed text-foreground">
        Life has been so happy lately. I find myself smiling for no reason, and I love that
        feeling. We don't talk enough about the happy seasons in life, and I think we should — it
        attracts more of it. What's your happy place right now? 😊
      </p>
      <div className="mt-3 flex items-center gap-4 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1">
          <Heart className="h-3 w-3 fill-red-500 text-red-500" /> 44
        </span>
        <span className="flex items-center gap-1">
          <MessageCircle className="h-3 w-3" /> 3 Replies
        </span>
      </div>
    </div>
    <div className="ml-4 rounded-lg border-l-2 border-border bg-paper p-3">
      <div className="flex items-center gap-1.5">
        <span className="h-4 w-4 rounded-full bg-mist" />
        <p className="text-[10px] font-medium text-brand">bright-owl-0701</p>
        <span className="text-[9px] text-muted-foreground">2 months ago</span>
      </div>
      <p className="mt-1 text-[10px] leading-relaxed text-foreground">
        I agree. I have regrets that I didn't appreciate my good times enough. So enjoy them
        while you are swimming either it. Life will always have stressors. It will never be
        perfect. It seems to me you learned what it took me so long to learn.
      </p>
      <div className="mt-2 flex items-center gap-4 text-[9px] text-muted-foreground">
        <Heart className="h-3 w-3" />
        <span className="flex items-center gap-1">
          <MessageCircle className="h-3 w-3" /> Reply
        </span>
      </div>
    </div>
    <div className="rounded-lg border border-border px-3 py-2 text-center text-[10px] text-muted-foreground">
      Write a reply...
    </div>
  </ScreenShell>
);


const Slider = ({ pct }: { pct: number }) => (
  <div className="relative mt-1.5 h-1.5 rounded-full bg-mist">
    <div className="absolute inset-y-0 left-0 rounded-full bg-brand" style={{ width: `${pct}%` }} />
    <span
      className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-brand bg-background"
      style={{ left: `${pct}%` }}
    />
  </div>
);

export const JournalScreen = () => (
  <ScreenShell title="Daily Journal">
    <div className="rounded-lg border border-border p-3">
      <div className="flex items-center gap-1.5">
        <Heart className="h-3 w-3 text-brand" />
        <p className="text-[11px] font-semibold text-foreground">Today's Mood Entry</p>
      </div>

      <div className="mt-3">
        <div className="flex items-center justify-between">
          <p className="text-[10px] text-foreground">Overall Mood</p>
          <p className="text-[10px] font-semibold text-foreground">Moderate 5/10</p>
        </div>
        <Slider pct={50} />
        <div className="mt-1 flex justify-between text-[8px] text-muted-foreground">
          <span>Very Low</span>
          <span>Excellent</span>
        </div>
      </div>

      <p className="mt-3 text-[10px] text-foreground">How are you feeling?</p>
      <div className="mt-1.5 flex flex-wrap gap-1">
        {["Happy", "Sad", "Anxious", "Calm", "Grateful", "Hopeful", "Tired", "Peaceful"].map((f) => (
          <span
            key={f}
            className="rounded-full border border-border px-2 py-0.5 text-[8px] font-medium text-foreground"
          >
            {f}
          </span>
        ))}
      </div>

      {[
        { label: "Energy Level", value: "3/5", pct: 60 },
        { label: "Sleep Last Night", value: "7 hours", pct: 50 },
        { label: "Stress Level", value: "3/5", pct: 55 },
      ].map((row) => (
        <div key={row.label} className="mt-3">
          <div className="flex items-center justify-between">
            <p className="text-[10px] text-foreground">{row.label}</p>
            <p className="text-[10px] font-semibold text-foreground">{row.value}</p>
          </div>
          <Slider pct={row.pct} />
        </div>
      ))}

      <p className="mt-3 text-[10px] text-foreground">Notes (Optional)</p>
      <div className="mt-1.5 rounded-md border border-border px-2 py-1.5 text-[9px] text-muted-foreground">
        What's on your mind today?
      </div>
    </div>
  </ScreenShell>
);


const checklistGroups = [
  {
    group: "Emotional Self-Care",
    items: [
      { task: "Listen to music", time: "15m", detail: "Play your favorite calming or uplifting music" },
      { task: "Practice gratitude", time: "5m", detail: "Write down 3 things you're grateful for" },
    ],
  },
  {
    group: "Mental Self-Care",
    items: [
      { task: "Meditate", time: "10m", detail: "Practice mindfulness or meditation" },
    ],
  },
];

export const ChecklistScreen = () => (
  <ScreenShell title="Self-Care Checklist">
    <div className="rounded-lg border border-border p-3">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-medium text-foreground">Today's Progress</p>
        <p className="text-[9px] text-muted-foreground">0 of 12 activities</p>
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-mist" />
    </div>

    {checklistGroups.map((g) => (
      <div key={g.group} className="rounded-lg border border-border p-3">
        <div className="flex items-center gap-1.5">
          <Heart className="h-3 w-3 text-brand" />
          <p className="text-[11px] font-semibold text-foreground">{g.group}</p>
        </div>
        <div className="mt-2 space-y-2">
          {g.items.map((item) => (
            <div key={item.task} className="rounded-md border border-border p-2">
              <div className="flex items-start gap-2">
                <span className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded-[3px] border border-border" />
                <div>
                  <div className="flex items-center gap-1.5">
                    <p className="text-[10px] font-medium text-foreground">{item.task}</p>
                    <span className="rounded-full border border-border px-1.5 py-0.5 text-[8px] text-muted-foreground">
                      {item.time}
                    </span>
                  </div>
                  <p className="mt-0.5 text-[9px] leading-relaxed text-muted-foreground">
                    {item.detail}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    ))}
  </ScreenShell>
);


export const CopingScreen = () => (
  <ScreenShell title="Coping Strategies">
    <div className="rounded-lg border border-border p-3">
      <div className="flex flex-wrap gap-1.5">
        {["All Strategies", "Breathing", "Grounding", "Behavioral", "Cognitive", "Mindfulness", "Physical"].map(
          (c, i) => (
            <span
              key={c}
              className={`rounded-md px-2 py-1 text-[9px] font-medium ${
                i === 0
                  ? "bg-brand text-brand-foreground"
                  : "border border-border text-foreground"
              }`}
            >
              {c}
            </span>
          ),
        )}
      </div>
    </div>

    <p className="text-[11px] font-semibold text-foreground">Featured Strategies</p>

    <div className="rounded-lg border border-border p-3">
      <div className="flex items-center justify-between">
        <span className="rounded-full border border-border px-2 py-0.5 text-[8px] font-medium text-foreground">
          Breathing
        </span>
        <span className="flex items-center gap-1 rounded-full bg-mist px-2 py-0.5 text-[8px] text-ink">
          <Sparkles className="h-2.5 w-2.5" /> Featured
        </span>
      </div>
      <p className="mt-2 text-[11px] font-semibold text-foreground">4-7-8 Breathing</p>
      <p className="mt-1 text-[9px] leading-relaxed text-muted-foreground">
        A simple breathing technique to reduce anxiety and promote calm
      </p>
      <div className="mt-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className="text-[9px] text-muted-foreground">5m</span>
          <span className="rounded-full bg-mist px-2 py-0.5 text-[8px] font-medium text-ink">Easy</span>
        </div>
        <span className="rounded-md bg-brand px-2.5 py-1 text-[9px] font-medium text-brand-foreground">
          Try It
        </span>
      </div>
    </div>
  </ScreenShell>
);


const sampleTherapists = [
  { name: "Dana M., LCSW", focus: "Anxiety • Trauma", location: "Austin, TX" },
  { name: "Marcus R., LPC", focus: "Men's mental health", location: "Chicago, IL" },
  { name: "Priya S., LMFT", focus: "Couples • Family", location: "Seattle, WA" },
  { name: "Dr. James L., PsyD", focus: "Depression • PTSD", location: "Denver, CO" },
  { name: "Aisha K., LCSW", focus: "Stress • Life transitions", location: "Atlanta, GA" },
];

export const DirectoryScreen = () => (
  <ScreenShell title="MENTAL HEALTH PROVIDERS">
    <div className="flex flex-wrap gap-1.5">
      {["All Providers", "Anxiety & Depression", "Trauma & PTSD", "Relationship Counseling"].map(
        (chip, i) => (
          <span
            key={chip}
            className={`rounded-full px-2 py-1 text-[8px] font-medium ${
              i === 0 ? "bg-brand text-brand-foreground" : "border border-border text-foreground"
            }`}
          >
            {chip}
          </span>
        ),
      )}
    </div>

    <div className="rounded-md border border-border px-2 py-1.5 text-[9px] text-muted-foreground">
      Search by name, specialty, or location
    </div>

    {sampleTherapists.map((t) => (
      <div key={t.name} className="flex items-start gap-3 rounded-lg border border-border p-3">
        <span className="h-9 w-9 shrink-0 rounded-full bg-mist" />
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-medium text-foreground">{t.name}</p>
          <p className="text-[10px] text-muted-foreground">{t.focus}</p>
          <p className="text-[9px] text-muted-foreground">{t.location}</p>
        </div>
      </div>
    ))}
  </ScreenShell>
);
